<template>
    <div v-if="currentExercise" class="weight-mode">
        <div class="wm-nav">
            <button-tooltip :tooltip="$t('common.previous')">
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-on="on" v-bind="attrs" depressed height="36" class="wm-nav-btn"
                        :disabled="isFirst || loadingComplete" @click="prevExercise">
                        <v-icon size="20">mdi-chevron-left</v-icon>
                    </v-btn>
                </template>
            </button-tooltip>

            <span class="wm-counter">{{ currentIndex + 1 }} / {{ localExercises.length }}</span>

            <button-tooltip :tooltip="isLast ? $t('common.finish') : $t('common.next')">
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-on="on" v-bind="attrs" depressed height="36" class="wm-nav-btn wm-nav-btn--next"
                        :loading="loadingComplete" @click="nextExercise">
                        <v-icon size="20">{{ isLast ? 'mdi-check' : 'mdi-chevron-right' }}</v-icon>
                    </v-btn>
                </template>
            </button-tooltip>
        </div>

        <v-carousel v-model="currentIndex" hide-delimiters height="auto" touchless :show-arrows="false">
            <v-carousel-item v-for="(exercise, i) in localExercises" :key="i">
                <div class="wm-slide">
                    <div class="wm-head">
                        <div class="wm-name">{{ exercise.name }}</div>
                        <div class="wm-sets">
                            <v-icon size="13" color="txtMuted">mdi-repeat</v-icon>
                            {{ getSetsString(exercise) }}
                        </div>
                    </div>

                    <div class="wm-media">
                        <youtube-iframe v-if="exercise.youtubeUrl" :link="{ videoId: exercise.youtubeUrl }"
                            :height="isMobile ? 250 : 400" />

                        <video-player v-else-if="exercise.video" :options="getVideoOptions(exercise)" />

                        <v-img v-else :src="exercise.img" class="wm-img" contain />
                    </div>

                    <div v-if="exercise?.commentary" class="wm-comment">
                        <v-icon size="16" color="accent">mdi-comment-text-outline</v-icon>
                        <div>
                            <span class="wm-comment-title">{{ $t('exercises.comment_of_coach') }}</span>
                            <div class="wm-comment-text">{{ exercise.commentary }}</div>
                        </div>
                    </div>

                    <div class="wm-card">
                        <edit-sets dialog-mode :config="exercise.config"
                            :assigned-routine-id="routine.assignedRoutineId" :exercise-id="exercise.id"
                            :block="exercise.block" :routine="routine" :blocks="routine.blocks"
                            :category="exercise.categories?.[0]" @updatedSet="onEditSets" />
                    </div>

                    <div class="wm-card">
                        <kimsa-textarea-field :label="$t('common.message_to_coach')"
                            :placeholder="$t('common.send_placeholder')" @change="commentaryFromStudent = $event"
                            :value="commentaryFromStudent" rows="2" />
                        <div class="wm-send">
                            <v-btn class="normal-btn wm-send-btn black--text" small depressed @click="sendToCoach"
                                :loading="loadingCommentary" :disabled="!canSendToCoach">
                                {{ $t('common.send') }}
                            </v-btn>
                        </div>
                    </div>
                </div>
            </v-carousel-item>
        </v-carousel>
        <v-sheet v-if="!isMobile" height="74" color="transparent" />

        <!-- FAB MENU -->
        <fab-menu :items="options" @click:timer="handleTimer" @click:exercise="handleExercise"
            @click:camera="handleCamera" @click:skip="handleSkip" @click:pain="handlePainScale"
            @click:history="handleHistory" withTooltips />

        <!-- DIALOGS -->
        <share-image-dialog ref="image" :image="cameraImage" :exercise="currentExercise" />
        <change-exercise-dialog ref="changeExercise" :exercise="currentExercise" @success="onChangeExerciseSuccess" />
        <timer-dialog ref="timer" :duration="-1" infinite closable />
        <pain-scale-dialog ref="painScale" @success="onPainScale" />
        <record-video-dialog ref="recordVideo" :exercise="currentExercise"
            :assigned-routine-id="routine.assignedRoutineId" />
        <exercise-history-dialog :routine="routine" ref="history" :exercise="currentExercise" />

        <!-- FOOTER -->
        <routine-footer :percent="percentReady" />
    </div>
</template>

<script>
import YoutubeIframe from '@/components/common/YoutubeIframe.vue'
import VideoPlayer from '@/components/common/VideoPlayer.vue'
import EditSets from "@/components/student/EditSets"
import FabMenu from "@/components/common/FabMenu.vue"

import ShareImageDialog from '@/components/common/ShareImageDialog.vue'
import ChangeExerciseDialog from '@/components/common/ChangeExerciseDialog.vue'
import TimerDialog from '@/components/common/routines/TimerDialog.vue'
import PainScaleDialog from '@/components/common/PainScaleDialog.vue'
import RecordVideoDialog from '@/components/common/RecordVideoDialog.vue'
import ExerciseHistoryDialog from '@/components/coach/routines/ExerciseHistoryDialog.vue'
import RoutineFooter from '@/components/common/routines/RoutineFooter'
import KimsaTextareaField from '../inputs/KimsaTextareaField.vue'
import ButtonTooltip from '@/components/common/ButtonTooltip'
import { db, FieldValue } from '../../../utils/Firebase'
import { buildSetsChange } from '@/utils/Routine'
import { clearBlocksAssignedRoutine } from '@/utils/Exercises'
import { sendMessage } from '@/utils/Messages'

export default {
    name: "WeightModeExercises",
    components: {
        YoutubeIframe,
        VideoPlayer,
        EditSets,
        FabMenu,
        ShareImageDialog,
        ChangeExerciseDialog,
        TimerDialog,
        PainScaleDialog,
        RecordVideoDialog,
        ExerciseHistoryDialog,
        RoutineFooter,
        KimsaTextareaField,
        ButtonTooltip
    },
    props: {
        exercises: {
            type: Array,
            default: () => [],
        },
        routine: {
            type: Object,
            default: null,
        }
    },
    data() {
        return {
            currentIndex: 0,
            localExercises: [],
            cameraImage: null,
            commentaryFromStudent: '',
            loadingCommentary: false,
            loadingComplete: false,
        }
    },
    computed: {
        canSendToCoach(vm = this) {
            return !!vm.commentaryFromStudent?.trim()
        },
        currentExercise(vm = this) {
            return vm.localExercises[vm.currentIndex] || null
        },
        percentReady(vm = this) {
            if (vm.routine?.advance?.post === true) return 100
            if (!vm.localExercises.length) return 0
            return parseInt(((vm.currentIndex) * 100) / vm.localExercises.length)
        },
        options(vm = this) {
            let opts = [
                { icon: 'mdi-timer-outline', value: 'timer', tooltip: vm.$t('common.timer') },
                { icon: 'mdi-swap-horizontal', value: 'exercise', tooltip: vm.$t('common.change') },
                { icon: 'mdi-camera', value: 'camera', tooltip: vm.$t('common.camera') },
                { icon: 'mdi-debug-step-over', value: 'skip', tooltip: vm.$t('common.skip') },
                { icon: 'mdi-history', value: 'history', tooltip: vm.$t('common.history') },
            ]

            if (vm.currentExercise?.painScale) {
                opts.push({ icon: '$painScale', value: 'pain', tooltip: vm.$t('common.pain_scale') })
            }

            return opts
        },
        studentId(vm = this) {
            return vm.currentUser.uid || null
        },
        coachId(vm = this) {
            return vm.account?.studentSubscription?.coachId || null
        },
        isFirst(vm = this) {
            return vm.currentIndex === 0
        },
        isLast(vm = this) {
            return vm.currentIndex === vm.localExercises.length - 1
        }
    },
    methods: {
        prevExercise() {
            if (this.isFirst) return
            this.currentIndex--
        },
        nextExercise() {
            if (this.isLast) {
                this.completeRoutine()
                return
            }
            this.currentIndex++
        },
        async completeRoutine() {
            const vm = this;
            await vm.withLoading(async () => {

            let routineRef = await db.collection('assignedRoutines')
                .doc(vm.routine.assignedRoutineId)
                .get()

            let assignedRoutine = routineRef.data()
            if (!assignedRoutine) return vm.notifyAssignedRoutineGone(vm.routine.assignedRoutineId)

            assignedRoutine.blocks = assignedRoutine.blocks.map(block => {
                block.completed = true

                block.exercises = block.exercises.map(exercise => {
                    if (exercise.type === 'exercise') exercise.completed = true
                    return exercise
                })

                return block
            })

            if (!await vm.saveAssignedRoutine(vm.routine.assignedRoutineId, { blocks: assignedRoutine.blocks })) return

            this.$emit('complete')
            }, 'loadingComplete')
        },
        getVideoOptions(exercise) {
            return {
                fluid: true,
                controls: true,
                autoplay: false,
                poster: exercise.video?.thumb || null,
                sources: [{
                    src: exercise.video?.url,
                    type: 'application/x-mpegURL'
                }]
            }
        },
        getSetsString(exercise) {
            const sets = exercise.config?.sets?.length || 0
            return `${sets} ${this.$tc('exercises.sets', (sets > 1 ? 2 : 1))}`
        },
        handleTimer() {
            this.$refs.timer.show()
        },
        handleExercise() {
            this.$refs.changeExercise.show()
        },
        handleCamera() {
            this.$refs.recordVideo.show()
        },
        async handleSkip() {
            const vm = this;
            const saved = await vm.saveAssignedRoutine(vm.routine.assignedRoutineId, {
                'advance.changes': FieldValue.arrayUnion({
                    type: 'skip',
                    block: vm.currentExercise.block,
                    exercise: vm.currentExercise.name,
                    exerciseId: vm.currentExercise.objectID || vm.currentExercise.id || null,
                })
            })
            if (!saved) return
            vm.toast(vm.$t('common.exercise_skipped'))
            vm.nextExercise()
        },
        handlePainScale() {
            this.$refs.painScale.show()
        },
        handleHistory() {
            this.$refs.history.show(this.currentExercise)
        },
        async onChangeExerciseSuccess({ oldExercise, newExercise }) {
            const vm = this;

            vm.$nextTick(() => {
                vm.localExercises[vm.currentIndex] = {
                    ...newExercise,
                    block: vm.currentExercise.block
                }
            })

            const saved = await vm.saveAssignedRoutine(vm.routine.assignedRoutineId, {
                'advance.changes': FieldValue.arrayUnion({
                    type: 'exercise',
                    block: vm.currentExercise.block,
                    exerciseId: oldExercise.objectID || oldExercise.id || null,
                    oldExercise: oldExercise.name,
                    newExercise: newExercise.name,
                })
            })
            if (!saved) return
            vm.toast(vm.$t('common.exercise_changed'))
        },
        async onEditSets(config) {
            const vm = this;

            const change = buildSetsChange({
                block: vm.currentExercise.block,
                exercise: vm.currentExercise.name,
                exerciseId: vm.currentExercise.objectID || vm.currentExercise.id || null,
                exerciseIndex: vm.currentIndex,
                previousConfig: vm.currentExercise.config,
                config,
                t: vm.$t.bind(vm),
            })

            if (change) {
                await vm.saveAssignedRoutine(vm.routine.assignedRoutineId, {
                    'advance.changes': FieldValue.arrayUnion(change)
                })
            }
        },
        async onPainScale(scale) {
            const vm = this;
            const saved = await vm.saveAssignedRoutine(vm.routine.assignedRoutineId, {
                'advance.changes': FieldValue.arrayUnion({
                    type: 'painScale',
                    block: vm.currentExercise.block,
                    exercise: vm.currentExercise.name,
                    exerciseId: vm.currentExercise.objectID || vm.currentExercise.id || null,
                    scale: scale
                })
            })
            if (!saved) return
            vm.toast(vm.$t('common.pain_scale_saved'))
        },
        async sendToCoach() {
            const vm = this
            if (!vm.canSendToCoach) return
            await vm.withLoading(async () => {
                let blocks = clearBlocksAssignedRoutine(vm.routine.blocks, false)
                let exerciseIndex = blocks[vm.currentExercise.block - 1].exercises.findIndex(ex => ex.id === vm.currentExercise.id)
                blocks[vm.currentExercise.block - 1].exercises[exerciseIndex].commentaryFromStudent = vm.commentaryFromStudent
                if (!await vm.saveAssignedRoutine(vm.routine.assignedRoutineId, { blocks: blocks })) return
                await vm.sendMessageToCoach()
                vm.toast(vm.$t('exercises.commentary_from_student_sent'))
            }, 'loadingCommentary')
        },
        async sendMessageToCoach() {
            const vm = this

            let message = `${vm.$t('common.date')}: ${vm.$moment().format('DD-MM-YYYY')} \n${vm.$tc('exercises.exercises', 1)}: ${vm.currentExercise.name} \n${vm.$t('common.comment')}: ${vm.commentaryFromStudent}`

            await sendMessage({ message, from: vm.studentId }, vm.coachId, vm.studentId)
        },
    },
    watch: {
        exercises: {
            immediate: true,
            handler(val) {
                this.localExercises = JSON.parse(JSON.stringify(val || []))
            }
        },
        currentExercise(val) {
            this.commentaryFromStudent = val?.commentaryFromStudent || ''
        }
    }
}
</script>

<style scoped>
.weight-mode {
    display: flex;
    flex-direction: column;
    padding-bottom: calc(74px + var(--safe-area-inset-bottom, 0px));
}

.wm-nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
}

.wm-nav-btn {
    min-width: 44px !important;
    border-radius: 10px !important;
    background: var(--v-surface2-base) !important;
    border: 1px solid var(--v-outline-base);
    color: var(--v-txtPrimary-base) !important;
}

.wm-nav-btn--next {
    background: var(--brand-gradient) !important;
    border-color: transparent;
    color: #000 !important;
}

.wm-counter {
    font-size: 12px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
    letter-spacing: 0.04em;
}

.wm-slide {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.wm-head {
    text-align: center;
}

.wm-name {
    font-size: 18px;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: var(--v-txtPrimary-base);
}

.wm-sets {
    margin-top: 4px;
    font-size: 12px;
    color: var(--v-txtMuted-base);
}

.wm-img {
    border-radius: 10px;
    max-height: 400px;
}

.wm-comment {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 14px;
    border-radius: 10px;
    border: 1px solid rgba(51, 153, 153, 0.3);
    background: rgba(51, 153, 153, 0.08);
}

.wm-comment-title {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--v-accent-base);
}

.wm-comment-text {
    font-size: var(--fs-body);
    color: var(--v-txtSecondary-base);
    line-height: 1.45;
}

.wm-card {
    padding: 14px;
    border-radius: 12px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface-base);
}

.wm-send {
    display: flex;
    justify-content: flex-end;
    margin-top: 12px;
}

.wm-send-btn {
    background: var(--brand-gradient) !important;
}
</style>